import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BaseEntityField, BaseInfoCompActionsSet, BaseInfoCompProps, BaseInfoCompSaveAction, FontIcon,
    useModeFormStyle, useModePanelStyle, InfoCompPanelHeader } from '@moderepo/mode-app-components';
import { Button, Checkbox, makeStyles, Paper, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { SortOrder } from '@moderepo/mode-apis';


const useStyle = makeStyles((theme: Theme) => {
    return {

        metricTable: {
            width: '100%',
        },

        metricRow: {
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
            borderBottom : `1px solid ${theme.palette.divider}`,
            marginBottom : theme.spacing(1),
            paddingBottom: theme.spacing(0.5),
            '&.hidden'   : {
                display: 'none',
            },
            '&.timestamp': {
                borderBottom: 'none',
                borderTop   : `1px solid ${theme.palette.divider}`,
                padding     : theme.spacing(1, 2),
                marginBottom: 0,
                fontWeight  : 'bold',
            },
        },

        metricNameCol: {
            textAlign: 'left',
            flex     : 1,
            color    : theme.palette.text.primary,
        },

        metricValueCol: {
            textAlign  : 'right',
            whiteSpace : 'nowrap',
            color      : theme.palette.text.secondary,
            paddingLeft: theme.spacing(1),

            '&.timestamp': {
                whiteSpace: 'nowrap',
            },
        },


        metricToggler: {
            padding: theme.spacing(0, 0, 0, 1),
        },
    };
});


export interface MetricInfo {
    readonly name: string;
    readonly value: string | number | undefined;
    readonly hidden?: boolean;
}


export interface RCMetricValuesDisplayActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate?: BaseInfoCompSaveAction<{
        readonly numericMetrics: {
            readonly [metricName: string]: boolean;
        };
        readonly tagMetrics: {
            readonly [metricName: string]: boolean;
        };
    }>
}


export interface RCMetricsValuesDisplayProps extends BaseInfoCompProps {
    readonly timestamp: string;
    readonly fieldsOrdering?: SortOrder;
    readonly fields: {
        readonly numericMetricFields: {
            readonly [metricName: string]: BaseEntityField<string>;
        },
        readonly tagMetricFields?: {
            readonly [metricName: string]: BaseEntityField<string>;
        },
    };
    readonly actions?: RCMetricValuesDisplayActionsSet;
    readonly text: {
        readonly updatedDate: string;
    }
    readonly noDataMessageComp: React.ReactNode;
}



/**
 * This is a dumb component used for displaying a set of metrics' values
 */
export const RCMetricsValuesDisplay: React.FC<RCMetricsValuesDisplayProps> = (props: RCMetricsValuesDisplayProps) => {

    const [hiddenMetrics, setHiddenMetrics] = useState<{
        readonly numericMetrics: {
            readonly [fieldKey: string]: boolean;
        };
        readonly tagMetrics: {
            readonly [fieldKey: string]: boolean;
        };
    }>({
        numericMetrics: {},
        tagMetrics    : {},
    });
    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();



    /**
     * Initialize the metric hidden map based on the fields' hidden flag
     */
    const initializeHiddenMetricsMap = useCallback(() => {
        setHiddenMetrics({
            numericMetrics: Object.keys(props.fields.numericMetricFields).reduce((
                result: {[fieldKey: string]: boolean},
                fieldKey: string,
            ): {[fieldKey: string]: boolean} => {
                return {
                    ...result,
                    [fieldKey]: Boolean(props.fields.numericMetricFields[fieldKey].hidden),
                };
            }, {}),
            tagMetrics: props.fields.tagMetricFields ? Object.keys(props.fields.tagMetricFields).reduce((
                result: {[fieldKey: string]: boolean},
                fieldKey: string,
            ): {[fieldKey: string]: boolean} => {
                return {
                    ...result,
                    [fieldKey]: Boolean(props.fields.tagMetricFields ? props.fields.tagMetricFields[fieldKey].hidden : true),
                };
            }, {}) : {},
        });
    }, [props.fields]);


    // On component init, initialize the hidden metric maps 1 time
    useEffect(() => {
        initializeHiddenMetricsMap();
    }, [initializeHiddenMetricsMap, props.fields]);


    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        initializeHiddenMetricsMap();
    }, [initializeHiddenMetricsMap]);



    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                numericMetrics: Object.keys(hiddenMetrics.numericMetrics).reduce((result: {[valueName: string]: boolean}, key: string) => {
                    if (hiddenMetrics.numericMetrics[key]) {
                        return {
                            ...result,
                            [key]: hiddenMetrics.numericMetrics[key],
                        };
                    }
                    return result;
                }, {}),
                tagMetrics: Object.keys(hiddenMetrics.tagMetrics).reduce((result: {[valueName: string]: boolean}, key: string) => {
                    if (hiddenMetrics.tagMetrics[key]) {
                        return {
                            ...result,
                            [key]: hiddenMetrics.tagMetrics[key],
                        };
                    }
                    return result;
                }, {}),
            });
        }

        return false;
    }, [props.actions, hiddenMetrics]);


    /**
     * A flag to check if we have data. We have data if we have at least 1 numericMetrics or tagMetrics.
     */
    const hasData = useMemo(() => {
        return (props.fields.numericMetricFields && Object.values(props.fields.numericMetricFields).length > 0)
        || (props.fields.tagMetricFields && Object.values(props.fields.tagMetricFields).length > 0);
    }, [props.fields]);


    /**
     * Sort's compare function by field labels
     */
    const compareLabels = useCallback((label1: string, label2: string): number => {
        if (props.fieldsOrdering === SortOrder.ASC) {
            if (label1 < label2) {
                return -1;
            }
            if (label1 > label2) {
                return 1;
            }
            return 0;
        }
        if (props.fieldsOrdering === SortOrder.DESC) {
            if (label1 < label2) {
                return 1;
            }
            if (label1 > label2) {
                return -1;
            }
        }
        return 0;
    }, [props.fieldsOrdering]);

    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                {hasData && (
                    <form
                        className={clsx(formClasses.root, props.formProps?.className)}
                        noValidate
                        autoComplete="on"
                        method="POST"
                        onSubmit={onFormSubmit}
                    >
                        <div className={classes.metricTable}>
                            {Object.keys(props.fields.numericMetricFields).sort((key1: string, key2: string) => {
                                if (props.fieldsOrdering) {
                                    const field1 = props.fields.numericMetricFields[key1];
                                    const field2 = props.fields.numericMetricFields[key2];
                                    return compareLabels(field1.label, field2.label);
                                }
                                return 0;
                            }).map((fieldKey: string) => {
                                const field = props.fields.numericMetricFields[fieldKey];
                                return (
                                    <div
                                        key={fieldKey}
                                        className={clsx(classes.metricRow, !props.isEditing && hiddenMetrics.numericMetrics[fieldKey] && 'hidden')}
                                    >
                                        <div className={classes.metricNameCol}>{field.label}</div>
                                        <div className={classes.metricValueCol}>{field.displayValue || field.value}</div>
                                        {props.isEditing && (
                                            <Checkbox
                                                className={classes.metricToggler}
                                                checked={!hiddenMetrics.numericMetrics[fieldKey]}
                                                value={!hiddenMetrics.numericMetrics[fieldKey]}
                                                color="primary"
                                                onChange={(event: React.ChangeEvent) => {
                                                    setHiddenMetrics({
                                                        ...hiddenMetrics,
                                                        numericMetrics: {
                                                            ...hiddenMetrics.numericMetrics,
                                                            [fieldKey]: !(event.target as HTMLInputElement).checked,
                                                        },
                                                    });
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            })}

                            {props.fields.tagMetricFields && (
                                <div className={classes.metricTable}>
                                    {Object.keys(props.fields.tagMetricFields).sort((key1: string, key2: string) => {
                                        if (props.fieldsOrdering && props.fields.tagMetricFields) {
                                            const field1 = props.fields.tagMetricFields[key1];
                                            const field2 = props.fields.tagMetricFields[key2];
                                            return compareLabels(field1.label, field2.label);
                                        }
                                        return 0;
                                    }).map((fieldKey: string) => {
                                        if (props.fields.tagMetricFields) {
                                            const field = props.fields.tagMetricFields[fieldKey];
                                            return (
                                                <div
                                                    key={fieldKey}
                                                    className={clsx(
                                                        classes.metricRow, !props.isEditing && hiddenMetrics.tagMetrics[fieldKey] && 'hidden',
                                                    )}
                                                >
                                                    <div className={classes.metricNameCol}>{field.label}</div>
                                                    <div className={classes.metricValueCol}>{field.displayValue || field.value}</div>
                                                    {props.isEditing && (
                                                        <Checkbox
                                                            className={classes.metricToggler}
                                                            checked={!hiddenMetrics.tagMetrics[fieldKey]}
                                                            value={!hiddenMetrics.tagMetrics[fieldKey]}
                                                            color="primary"
                                                            onChange={(event: React.ChangeEvent) => {
                                                                setHiddenMetrics({
                                                                    ...hiddenMetrics,
                                                                    tagMetrics: {
                                                                        ...hiddenMetrics.tagMetrics,
                                                                        [fieldKey]: !(event.target as HTMLInputElement).checked,
                                                                    },
                                                                });
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        }

                                        return (<></>);
                                    })}
                                </div>
                            )}
                        </div>

                        {props.isEditing && props.actions && (
                            <div className={clsx(formClasses.formActionContainer)}>
                                <div />
                                <div>
                                    {props.actions?.cancelEdit && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            size="small"
                                            startIcon={props.actions.cancelEdit.icon
                                                ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                            onClick={() => {
                                                reverseFormInput();
                                                if (props.actions?.cancelEdit) {
                                                    props.actions.cancelEdit.onClick();
                                                }
                                            }}
                                        >
                                            {props.actions.cancelEdit.label}
                                        </Button>
                                    )}
                                    {props.actions?.saveUpdate && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            size="small"
                                            startIcon={props.actions.saveUpdate.icon
                                                ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                        >
                                            {props.actions.saveUpdate.label}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </form>
                )}

                {!hasData && props.noDataMessageComp}
            </div>

            {!props.isEditing && hasData && (
                <div className={clsx(classes.metricRow, 'timestamp')}>
                    <div className={clsx(classes.metricNameCol)}>{props.text.updatedDate}</div>
                    <div className={clsx(classes.metricValueCol, 'timestamp')}>{props.timestamp}</div>
                </div>
            )}

        </Paper>
    );

};
